import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox } from "lucide-react";
import { Logo } from "@/components/landing/Nav";
import { API } from "@/config";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("mp_admin_token"));
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("mp_admin_token", data.token);
      setToken(data.token);
    } catch {
      toast.error("Password non corretta");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLeads(res.data))
      .catch(() => {
        localStorage.removeItem("mp_admin_token");
        setToken(null);
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem("mp_admin_token");
    setToken(null);
    setLeads(null);
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-[#09090B] flex items-center justify-center px-6 font-jakarta">
        <form
          onSubmit={login}
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur-md p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3 mb-8">
            <Logo size={40} />
            <div>
              <p className="font-syne font-bold text-zinc-50">Area Riservata</p>
              <p className="text-xs text-zinc-500">MPX Digital Admin</p>
            </div>
          </div>
          <label htmlFor="admin-password" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-zinc-900/70 px-4 py-3 text-sm text-zinc-100 outline-none transition-all focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20"
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-6 py-3 font-semibold text-zinc-950 transition-all hover:bg-cyan-300 disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Accedi
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#09090B] text-zinc-50 font-jakarta" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div>
              <h1 className="font-syne text-2xl font-bold">Richieste ricevute</h1>
              <p className="text-sm text-zinc-500">
                {leads ? `${leads.length} contatti dal sito` : "Caricamento…"}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-400 hover:border-red-400/50 hover:text-red-300 transition-colors"
            data-testid="admin-logout-button"
          >
            <LogOut size={15} />
            Esci
          </button>
        </div>

        {!leads ? (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-cyan-400" />
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-zinc-900/60 py-20 text-zinc-500" data-testid="admin-leads-empty">
            <Inbox size={32} />
            <p>Nessuna richiesta per ora. Arriveranno presto.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-leads-list">
            {leads.map((l) => (
              <article
                key={l.id}
                className="rounded-2xl border border-white/10 bg-zinc-900/60 p-6"
                data-testid={`admin-lead-${l.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="font-syne text-lg font-semibold">{l.name}</h2>
                    <a href={`mailto:${l.email}`} className="text-sm text-cyan-400 hover:text-cyan-300">
                      {l.email}
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-cyan-400/25 bg-cyan-400/5 px-3 py-1 text-xs text-cyan-300">
                      {l.business_type}
                    </span>
                    {l.budget && (
                      <span className="rounded-full border border-amber-400/25 bg-amber-400/5 px-3 py-1 text-xs text-amber-300">
                        {l.budget}
                      </span>
                    )}
                    <span className="font-mono text-xs text-zinc-500">
                      {new Date(l.created_at).toLocaleString("it-IT")}
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-sm text-zinc-400 leading-relaxed whitespace-pre-wrap">{l.message}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
